import React, { useEffect } from 'react'
import { UseFormReturn, Controller, useFieldArray } from 'react-hook-form'
import { WidgetFormValues } from '@/lib/widget-schema'
import { useTranslation } from '@/hooks/useTranslation'
import {
	Card,
	CardContent,
	CardHeader,
	CardTitle,
	CardDescription,
} from '@/components/ui/card'
import {
	FormField,
	FormItem,
	FormLabel,
	FormControl,
	FormDescription,
	FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/components/ui/select'
import { Checkbox } from '@/components/ui/checkbox'
import { Button } from '@/components/ui/button'
import { ExternalLink, Plus, Trash2, Info } from 'lucide-react'
import { LanguageConfigurationTip } from '@/components/ui/language-tip'

interface Step4Props {
	form: UseFormReturn<WidgetFormValues>
	isPremium: boolean
}

const deviceTypes = ['desktop', 'mobile', 'tablet'] as const
type DeviceType = (typeof deviceTypes)[number]

export function Step4_Behavior({ form, isPremium }: Step4Props) {
	const { t } = useTranslation()
	const triggerType = form.watch('trigger.type')

	// Field array for user properties (commented out as per your example)
	/*
  const { fields, append, remove } = useFieldArray({
	control: form.control,
	name: "targeting.userPropertiesArray" // Use a temporary array structure for easier UI management
  });

  // Sync userPropertiesArray back to the object format expected by the schema
  // This should ideally happen before submitting the form
  const syncUserProperties = () => {
	const arrayData = form.getValues('targeting.userPropertiesArray');
	const objectData: Record<string, string> = {};
	if (Array.isArray(arrayData)) {
		arrayData.forEach(item => {
			if (item.key && item.value) { // Ensure both key and value exist
				objectData[item.key] = item.value;
			}
		});
	}
	form.setValue('targeting.userProperties', objectData, { shouldValidate: false, shouldDirty: true });
  };

  // Initialize the array form field if userProperties exists
  useEffect(() => {
	const currentProps = form.getValues('targeting.userProperties');
	if (currentProps && Object.keys(currentProps).length > 0 && fields.length === 0) {
		const initialArray = Object.entries(currentProps).map(([key, value]) => ({ key, value }));
		form.setValue('targeting.userPropertiesArray' as any, initialArray); // Use 'as any' if TS complains, or adjust schema
	}
  }, [form, fields.length]);
  */

	return (
		<Card>
			<CardHeader>
				<CardTitle>{t('widgetBuilder.step4.title')}</CardTitle>
				<CardDescription>
					{t('widgetBuilder.step4.description')}
				</CardDescription>
			</CardHeader>
			<CardContent className="space-y-6">
				{/* === Trigger Settings === */}
				<div className="space-y-4">
					<h3 className="text-md font-semibold">{t('widgetBuilder.step4.trigger.title')}</h3>
					<FormField
						control={form.control}
						name="trigger.type"
						render={({ field }) => (
							<FormItem>
								<FormLabel>{t('widgetBuilder.step4.trigger.triggerType')}</FormLabel>
								<FormDescription className="text-xs">
									{t('widgetBuilder.step4.trigger.triggerTypeDescription')}
								</FormDescription>
								<Select value={field.value} onValueChange={field.onChange}>
									<FormControl>
										<SelectTrigger>
											<SelectValue placeholder={t('widgetBuilder.step4.trigger.selectPlaceholder')} />
										</SelectTrigger>
									</FormControl>
									<SelectContent>
										<SelectItem value="immediate">{t('widgetBuilder.step4.trigger.options.immediate')}</SelectItem>
										<SelectItem value="delay">{t('widgetBuilder.step4.trigger.options.delay')}</SelectItem>
										<SelectItem value="scroll">{t('widgetBuilder.step4.trigger.options.scroll')}</SelectItem>
										<SelectItem value="exit">{t('widgetBuilder.step4.trigger.options.exit')}</SelectItem>
										<SelectItem value="manual">{t('widgetBuilder.step4.trigger.options.manual')}</SelectItem>
									</SelectContent>
								</Select>
							</FormItem>
						)}
					/>

					{triggerType === 'delay' && (
						<FormField
							control={form.control}
							name="trigger.delay"
							render={({ field }) => (
								<FormItem>
									<FormLabel>{t('widgetBuilder.step4.trigger.delay.label')}</FormLabel>
									<FormDescription className="text-xs">
										{t('widgetBuilder.step4.trigger.delay.description')}
									</FormDescription>
									<FormControl>
										<Input
											type="number"
											min={0}
											placeholder="e.g., 5"
											{...field}
											onChange={(e) => field.onChange(Number(e.target.value))}
										/>
									</FormControl>
								</FormItem>
							)}
						/>
					)}

					{triggerType === 'scroll' && (
						<FormField
							control={form.control}
							name="trigger.scrollPercentage"
							render={({ field }) => (
								<FormItem>
									<FormLabel>{t('widgetBuilder.step4.trigger.scroll.label')}</FormLabel>
									<FormDescription className="text-xs">
										{t('widgetBuilder.step4.trigger.scroll.description')}
									</FormDescription>
									<FormControl>
										<Input
											type="number"
											min={0}
											max={100}
											placeholder="e.g., 50"
											{...field}
											onChange={(e) => field.onChange(Number(e.target.value))}
										/>
									</FormControl>
								</FormItem>
							)}
						/>
					)}

					{triggerType === 'exit' && (
						<div className="rounded-lg border bg-muted/50 p-4">
							<p className="text-sm text-muted-foreground">
								{t('widgetBuilder.step4.trigger.exit.description')}
							</p>
						</div>
					)}

					{triggerType === 'manual' && (
						<div className="rounded-lg border bg-muted/50 p-4 space-y-3">
							<p className="text-sm text-muted-foreground">
								{t('widgetBuilder.step4.trigger.manual.description')}
							</p>
							<div className="space-y-2">
								<label className="text-sm font-medium">{t('widgetBuilder.step4.trigger.manual.integrationCode')}</label>
								<div className="bg-gray-900 text-green-400 p-3 rounded-md font-mono text-sm">
									<code>window.feedbask.open()</code>
								</div>
							</div>
							<p className="text-xs text-muted-foreground">
								{t('widgetBuilder.step4.trigger.manual.hint')}
							</p>
							<div className="pt-2">
								<Button
									variant="outline"
									size="sm"
									onClick={() =>
										window.open('/docs#programmatic-triggers', '_blank')
									}
									className="text-xs">
									<ExternalLink className="h-3 w-3 mr-1" />
									{t('widgetBuilder.step4.trigger.manual.viewDocumentation')}
								</Button>
							</div>
						</div>
					)}
				</div>

				{/* === Targeting Settings === */}
				<div className="space-y-4 pt-4 border-t">
					<h3 className="text-md font-semibold">{t('widgetBuilder.step4.targeting.title')}</h3>

					{/* URL Patterns */}
					<FormField
						control={form.control}
						name="targeting.urlPatterns"
						render={({ field }) => {
							// Add a state to track the input value
							const [inputValue, setInputValue] = React.useState(
								field.value?.join(', ') || ''
							)

							// Update the state when the field value changes
							React.useEffect(() => {
								setInputValue(field.value?.join(', ') || '')
							}, [field.value])

							return (
								<FormItem>
									<FormLabel>{t('widgetBuilder.step4.targeting.showOnUrls.label')}</FormLabel>
									<FormDescription className="text-xs">
										{t('widgetBuilder.step4.targeting.showOnUrls.description')}
									</FormDescription>
									<FormControl>
										<Input
											placeholder="/docs/*, /pricing"
											value={inputValue}
											onChange={(e) => {
												// Just update the input value, don't process yet
												setInputValue(e.target.value)
											}}
											onBlur={() => {
												// Process the comma-separated values on blur
												const patterns = inputValue
													.split(',')
													.map((s) => s.trim())
													.filter((s) => s.length > 0)
												field.onChange(patterns)
											}}
										/>
									</FormControl>
									<FormMessage />
								</FormItem>
							)
						}}
					/>

					{/* Exclude URL Patterns */}
					<FormField
						control={form.control}
						name="targeting.excludeUrlPatterns"
						render={({ field }) => {
							// Add a state to track the input value
							const [inputValue, setInputValue] = React.useState(
								field.value?.join(', ') || ''
							)

							// Update the state when the field value changes
							React.useEffect(() => {
								setInputValue(field.value?.join(', ') || '')
							}, [field.value])

							return (
								<FormItem>
									<FormLabel>{t('widgetBuilder.step4.targeting.excludeOnUrls.label')}</FormLabel>
									<FormDescription className="text-xs">
										{t('widgetBuilder.step4.targeting.excludeOnUrls.description')}
									</FormDescription>
									<FormControl>
										<Input
											placeholder="/admin/*, /login"
											value={inputValue}
											onChange={(e) => {
												// Just update the input value, don't process yet
												setInputValue(e.target.value)
											}}
											onBlur={() => {
												// Process the comma-separated values on blur
												const patterns = inputValue
													.split(',')
													.map((s) => s.trim())
													.filter((s) => s.length > 0)
												field.onChange(patterns)
											}}
										/>
									</FormControl>
									<FormMessage />
								</FormItem>
							)
						}}
					/>

					{/* Device Types */}
					<div className="space-y-2">
						<FormLabel>{t('widgetBuilder.step4.targeting.deviceTypes.label')}</FormLabel>
						<FormDescription className="text-xs">
							{t('widgetBuilder.step4.targeting.deviceTypes.description')}
						</FormDescription>
						<div className="flex flex-wrap gap-4 pt-2">
							{deviceTypes.map((device) => (
								<FormField
									key={device}
									control={form.control}
									name="targeting.deviceTypes"
									render={({ field }) => (
										<FormItem className="flex flex-row items-center space-x-2 space-y-0">
											<FormControl>
												<Checkbox
													checked={field.value?.includes(device)}
													onCheckedChange={(checked) => {
														const current = field.value || []
														const updated = checked
															? [...current, device]
															: current.filter((d) => d !== device)
														// Ensure at least one device is selected if possible
														field.onChange(
															updated.length > 0 ? updated : current
														)
													}}
												/>
											</FormControl>
											<FormLabel className="font-normal capitalize text-sm">
												{t(`targetingOptions.${device}`)}
											</FormLabel>
										</FormItem>
									)}
								/>
							))}
						</div>
						<FormMessage /> {/* For potential validation */}
					</div>

					{/* User Properties (Example, assuming simple key-value) */}
					{/* Note: Advanced user property targeting might require a more complex UI */}
					{/* <div className="space-y-2">
                <FormLabel>User Properties (Advanced)</FormLabel>
                 <FormDescription className="text-xs">
                    Target based on custom data you send (e.g., plan=premium). Requires code integration.
                 </FormDescription>
                 {fields.map((fieldItem, index) => (
                    <div key={fieldItem.id} className="flex items-center gap-2">
                        <FormField
                            control={form.control}
                            name={`targeting.userPropertiesArray.${index}.key`}
                            render={({ field }) => (
                                <FormItem className="flex-1">
                                    <FormControl><Input {...field} placeholder="Property Name" /></FormControl>
                                </FormItem>
                            )}
                            />
                         <FormField
                            control={form.control}
                            name={`targeting.userPropertiesArray.${index}.value`}
                            render={({ field }) => (
                                <FormItem className="flex-1">
                                    <FormControl><Input {...field} placeholder="Property Value" /></FormControl>
                                </FormItem>
                            )}
                            />
                        <Button variant="ghost" size="icon" onClick={() => remove(index)}><Trash2 className="h-4 w-4" /></Button>
                    </div>
                 ))}
                 <Button type="button" variant="outline" size="sm" onClick={() => append({ key: '', value: '' })}> 
                     <Plus className="mr-2 h-4 w-4" /> Add Property
                 </Button>
            </div> */}
				</div>

				{/* === User Identification Section === */}
				<div className="space-y-4 pt-4 border-t">
					<div className="p-4 bg-blue-50 dark:bg-blue-950/30 rounded-xl border border-blue-200 dark:border-blue-800/50">
						<div className="flex items-start gap-3">
							<div className="p-2 bg-blue-100 dark:bg-blue-900/50 rounded-lg flex-shrink-0">
								<Info className="h-4 w-4 text-blue-600 dark:text-blue-400" />
							</div>
							<div className="flex-1 min-w-0 space-y-4">
								<h4 className="text-sm font-semibold text-blue-900 dark:text-blue-100 mb-1">
									{t('widgetBuilder.step4.userIdentification.title')}
								</h4>
								<p className="text-sm text-blue-700 dark:text-blue-300">
									{t('widgetBuilder.step4.userIdentification.description')}
								</p>
						
								<div className="grid md:grid-cols-2 gap-4 text-sm">
									<div className="space-y-2">
										<h5 className="font-medium text-blue-900 dark:text-blue-100">{t('widgetBuilder.step4.userIdentification.benefits.title')}</h5>
										<ul className="text-blue-700 dark:text-blue-300 space-y-1 text-xs">
											<li>• {t('widgetBuilder.step4.userIdentification.benefits.item1')}</li>
											<li>• {t('widgetBuilder.step4.userIdentification.benefits.item2')}</li>
										</ul>
									</div>
									<div className="space-y-2">
										<h5 className="font-medium text-blue-900 dark:text-blue-100">{t('widgetBuilder.step4.userIdentification.implementation.title')}</h5>
										<div className="bg-white dark:bg-blue-900/30 border border-blue-200 dark:border-blue-700/50 rounded p-3 font-mono text-xs">
											<code className="text-blue-700 dark:text-blue-300">
												window.feedbask.identify(&#123;<br/>
												&nbsp;&nbsp;userId: 'user_123',<br/>
												&nbsp;&nbsp;name: 'John Doe',<br/>
												&nbsp;&nbsp;email: 'john@example.com'<br/>
												&#125;)
											</code>
										</div>
									</div>
								</div>

								<div className="pt-2">
									<Button
										variant="outline"
										size="sm"
										onClick={() => window.open('/docs#user-identification', '_blank')}
										className="text-xs text-blue-600 dark:text-blue-400 border-blue-200 dark:border-blue-700/50 hover:bg-blue-50 dark:hover:bg-blue-900/20">
										<ExternalLink className="h-3 w-3 mr-1" />
										{t('widgetBuilder.step4.userIdentification.viewGuide')}
									</Button>
								</div>
							</div>
						</div>
					</div>
				</div>

				{/* Language Configuration Tips */}
				<LanguageConfigurationTip className="mt-6" />
			</CardContent>
		</Card>
	)
}
